import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SaveButton from "../../components/button/SaveButton";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import SegmentedTabs from "../../components/tabs/SegmentedTabs";
import BuildingInfoTab from "./tabs/BuildingInfoTab";
// import SupplierInfoTab from "./tabs/SupplierInfoTab";
import { useColorMode } from "../../theme/ThemeProviderWrapper";
import AlternativesTab from "./tabs/AlternativesTab";
import ResultTab from "./tabs/ResultTab";
import WBLCATab from "./tabs/WBLCATab";

import { MainContainer } from "../../components/container/MainContainer";
import ExportBuildingPPTButton from "../../components/export/ExportBuildingPPTButton";
import { ORG_PALETTE, getOrgTokens } from "../../theme/style/organizationPalette";
import routing from "../../routing";
import { useBuildingById } from "../../utilities/useBuildingQueries";
import BIMUniformatTab from "./tabs/BIMUniformatTab";
import muiSytle from "../../style/components-style/muiStyle";

const BuildingWorkspace = () => {
  const {
    selectedBuildings,
    setIsSaveVisible,
    setIsLoading,
    setIsSaveClicked,
  } = useContextProvider();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Building Info");
  const theme = useTheme();
  const { mode } = useColorMode();
  const isDark = theme.palette.mode === "dark";

  // LIFTED STATE: building section selected and setters
  const [selectedBuildingSection, setSelectedBuildingSection] = useState(null);

  // Derive building ID from context selection
  const buildingId = selectedBuildings.length === 1 ? selectedBuildings[0].id : null;

  // CACHED: Fetch fresh building data — mirrors useProjectById in ProjectWorkspace
  const { data: selectedBuilding, isLoading: isBuildingLoading } = useBuildingById(buildingId);

  // Redirect to dashboard when no building is selected (e.g. after page refresh)
  // Matches the pattern used by ProductWorkspace and ProjectWorkspace exactly.
  useEffect(() => {
    if (selectedBuildings.length !== 1) {
      navigate(routing.sector.buildings.dashboard);
    }
  }, [selectedBuildings, navigate]);

  // Sync loading state and reset save flags — mirrors ProjectWorkspace
  useEffect(() => {
    if (selectedBuilding) {
      setIsSaveVisible(false);
      setIsLoading(false);
      setIsSaveClicked(false);
    } else if (isBuildingLoading) {
      setIsLoading(true);
    }
  }, [selectedBuilding, isBuildingLoading, setIsLoading, setIsSaveClicked, setIsSaveVisible]);

  // Tabs array
  const tabs = [
    { label: "Building Info", value: "Building Info" },
    { label: "WBLCA", value: "WBLCA" },
    { label: "Result", value: "Result" },
    { label: "Alternatives", value: "Alternatives" },
  ];

  return (
    <MainContainer>
      <Box sx={{ display: selectedBuilding ? "block" : "none" }}>
        {/* HEADER WITH SEGMENTED TABS */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: ORG_PALETTE.spacing.sectionGap }}>
          <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
            <Box sx={{ width: "100%", maxWidth: "800px" }}>
              <SegmentedTabs options={tabs} selected={activeTab} setSelected={setActiveTab} />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <ExportBuildingPPTButton />
          </Box>
        </Box>

        {/* TAB CONTENT */}
        <Box sx={{ paddingTop: 0 }}>
          <Box sx={{
            display: activeTab === "Building Info" ? "block" : "none",
            animation: "org-fadeInUp 0.4s ease"
          }}>
            <BuildingInfoTab building={selectedBuilding} />
          </Box>


          <Box sx={{
            display: activeTab === "WBLCA" ? "block" : "none",
            animation: "org-fadeInUp 0.4s ease"
          }}>
            <WBLCATab building={selectedBuilding} selectedBuildingSection={selectedBuildingSection} setSelectedBuildingSection={setSelectedBuildingSection} />
          </Box>

          <Box sx={{
            display: activeTab === "Result" ? "block" : "none",
            animation: "org-fadeInUp 0.4s ease"
          }}>
            <ResultTab building={selectedBuilding} />
          </Box>

          <Box sx={{
            display: activeTab === "Alternatives" ? "block" : "none",
            animation: "org-fadeInUp 0.4s ease"
          }}>
            <AlternativesTab building={selectedBuilding} />
          </Box>
        </Box>
      </Box>

      {/* GLOBAL SAVE BUTTON */}
      <SaveButton />
    </MainContainer>
  );
};

export default BuildingWorkspace;
